import HERO_IMG from "@/assets/banyan-tree.webp";
import { publicPath } from "@/lib/seo";
import { t, useLanguage } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GuestTreeDialog from "@/components/GuestTreeDialog";
import HowToVideos from "@/components/HowToVideos";
import LandingAdvertisements from "@/components/LandingAdvertisements";
import HeaderAccount from "@/components/HeaderAccount";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  TreePine, ShieldCheck, Users, BadgeCheck, Lock, Smartphone,
  ScanFace, Share2, ZoomIn, ArrowRight, Menu, X,
} from "lucide-react";


const ALBUM_IMG = "/security-photo-album.webp";

const features = [
  { icon: TreePine, title: "Ancestor Tree", desc: "Build an interactive multi-generation lineage with parent, child and spouse links." },
  { icon: Users, title: "Rich Profiles", desc: "Name, gotra and gotra, birth date, gender, living status, photos, videos and social links." },
  { icon: Share2, title: "Granular Access", desc: "Invite family by email as Viewer, Editor or Co-Admin. You control every record." },
  { icon: ZoomIn, title: "Zoom & Pan", desc: "Explore huge family trees with fluid zoom, pan and fit-to-screen controls." },
  { icon: Smartphone, title: "Mobile Ready", desc: "Cloud storage keeps everything in sync for the upcoming iOS & Android apps." },
];

const security = [
  { icon: Lock, title: "Private Family Memories", desc: "Share family photos and stories with the people you choose." },
  { icon: ScanFace, title: "Role-Based Access", desc: "Every record enforces owner-defined permissions on the server." },
  { icon: ShieldCheck, title: "Leak Protection", desc: "JWT auth, brute-force lockout and origin-locked APIs guard your data." },
];

export default function Landing() {
  const language = useLanguage();
  const { user, logout } = useAuth();
  const signedIn = Boolean(user && user.role !== "guest");
  const [guestOpen, setGuestOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const dest = signedIn ? "/app" : "/register";

  const continueAsGuest = () => { setMenu(false); setGuestOpen(true); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen parchment-grain">
      {/* header */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-md bg-[#2C1E16]/95 shadow-lg" : "bg-transparent"
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <nav className="hidden md:flex items-center gap-8 text-sm text-amber-100/80">
            <a href="#features" className="hover:text-[#D4A359] transition-colors">{t("Features")}</a>
            <a href="#how-to" className="hover:text-[#D4A359] transition-colors" onClick={() => setMenu(false)}>{t("How to")}</a>
            <a href="#security" className="hover:text-[#D4A359] transition-colors">{t("Security")}</a>
            <a href="#mobile" className="hover:text-[#D4A359] transition-colors">{t("Mobile App")}</a>
            <Link to={publicPath("/about", language)} className="hover:text-[#D4A359] transition-colors">{t("About")}</Link>
            <Link to={publicPath("/contact", language)} className="hover:text-[#D4A359] transition-colors">{t("Contact")}</Link>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {signedIn ? (
              <div className="flex items-center gap-3">
                <span className="text-amber-100 text-sm" data-testid="landing-user-name">{user.name || user.email}</span>
                <Link to="/app"><Button className="gold-btn text-[#2C1E16] font-semibold border-0" data-testid="header-open-app-button">{t("Open Tree")}</Button></Link>
                <Button variant="ghost" className="text-amber-50 hover:text-[#D4A359] hover:bg-transparent" onClick={logout} data-testid="header-signout-button">{t("Sign out")}</Button>
              </div>
            ) : (
              <>
                <Link to="/register"><Button className="gold-btn text-[#2C1E16] font-semibold border-0" data-testid="header-signup-button">{t("Create Account")}</Button></Link>
                <Link to="/login"><Button variant="ghost" className="text-amber-50 hover:text-[#D4A359] hover:bg-transparent" data-testid="header-login-button">{t("Sign in")}</Button></Link>
                <Button variant="ghost" className="text-amber-50 hover:text-[#D4A359] hover:bg-transparent" onClick={continueAsGuest} data-testid="header-guest-button">{t("Continue As Guest")}</Button>
              </>
            )}
          </div>
          <div className="md:hidden flex items-center gap-1 ml-auto">
          <HeaderAccount className="text-amber-50 hover:bg-white/10" />
          <button className="md:hidden text-amber-50 min-w-11 min-h-11 flex items-center justify-center" aria-label={menu ? t("Close navigation") : t("Open navigation")} aria-expanded={menu} aria-controls="landing-mobile-menu" onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle">
            {menu ? <X /> : <Menu />}
          </button>
          </div>
        </div>
        {menu && (
          <div id="landing-mobile-menu" className="md:hidden bg-[#2C1E16] px-5 pb-4 flex flex-col gap-3 text-amber-100/90">
            <a href="#features" onClick={() => setMenu(false)}>{t("Features")}</a>
            <a href="#how-to" className="hover:text-[#D4A359] transition-colors" onClick={() => setMenu(false)}>{t("How to")}</a>
            <a href="#security" onClick={() => setMenu(false)}>{t("Security")}</a>
            <a href="#mobile" onClick={() => setMenu(false)}>{t("Mobile App")}</a>
            <Link to={publicPath("/about", language)} className="hover:text-[#D4A359] transition-colors">{t("About")}</Link>
            <Link to={publicPath("/contact", language)} onClick={() => setMenu(false)}>{t("Contact")}</Link>
            {signedIn ? (
              <>
                <span className="text-sm text-amber-100" data-testid="mobile-user-name">{user.name || user.email}</span>
                <Link to="/app" onClick={() => setMenu(false)}><Button className="gold-btn text-[#2C1E16] font-semibold border-0 w-full">{t("Open Tree")}</Button></Link>
                <Button variant="ghost" className="text-amber-50 justify-start" onClick={logout} data-testid="mobile-signout-button">{t("Sign out")}</Button>
              </>
            ) : (
              <>
                <Link to="/register" onClick={() => setMenu(false)}><Button className="gold-btn text-[#2C1E16] font-semibold border-0 w-full">{t("Create Account")}</Button></Link>
                <Link to="/login" onClick={() => setMenu(false)} className="py-2">{t("Sign in")}</Link>
                <Button variant="ghost" className="text-amber-50 justify-start" onClick={continueAsGuest} data-testid="mobile-guest-button">{t("Continue As Guest")}</Button>
              </>
            )}
          </div>
        )}
      </header>

      {/* hero */}
      <section className="relative bg-[#2C1E16] text-amber-50 pt-28 pb-24 overflow-hidden">
        <div className="parchment-grain absolute inset-0 opacity-30" />
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#D4A359]">{t("Secure Ancestor Vault")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-4">{t("Preserve every branch of your ")}<span className="text-[#D4A359] italic">{t("family lineage.")}</span>
            </h1>
            <p className="mt-6 text-lg text-amber-100/70 max-w-xl leading-relaxed">{t("Build a zoomable ancestor tree, attach verified identities, photos and memories — protected in an encrypted vault, ready for the mobile era.")}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to={dest}>
                <Button className="gold-btn text-[#2C1E16] font-semibold border-0 h-12 px-7 text-base" data-testid="hero-cta-button">{t("Start Your Tree ")}<ArrowRight className="ml-1" size={18} />
                </Button>
              </Link>
              <Button variant="outline" className="h-12 px-7 text-base border-amber-100/30 text-amber-50 bg-transparent hover:bg-amber-50/10" onClick={continueAsGuest} data-testid="hero-explore-vansavali-button">{t("Explore Vansavali")}</Button>
              <a href="#features">
                <Button variant="outline" className="h-12 px-7 text-base border-amber-100/30 text-amber-50 bg-transparent hover:bg-amber-50/10" data-testid="hero-learn-button">{t("Explore Features")}</Button>
              </a>
            </div>
            <div className="mt-10 flex items-center gap-3 text-sm text-amber-100/60">
              <ShieldCheck size={18} className="text-[#D4A359]" />{t(" End-to-end encryption · Role-based sharing · GDPR aware")}</div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-[#D4A359]/40 shadow-2xl">
              <img src={HERO_IMG} width={1536} height={1024} fetchpriority="high" alt={t("Banyan tree representing family lineage")} className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* features */}
      <LandingAdvertisements />
      <section id="features" className="max-w-7xl mx-auto px-5 py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-800/80">{t("Everything your family needs")}</p>
          <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-stone-900">{t("A living archive of who you are.")}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="group bg-white rounded-2xl p-7 border border-[#E6DEC3] hover:border-[#D4A359] hover:shadow-xl transition-all duration-300"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#F4EFEB] flex items-center justify-center text-amber-800 group-hover:bg-[#D4A359] group-hover:text-[#2C1E16] transition-colors">
                <f.icon size={22} />
              </div>
              <h3 className="font-serif text-xl font-semibold mt-5 text-stone-900">{t(f.title)}</h3>
              <p className="text-stone-600 mt-2 leading-relaxed text-sm">{t(f.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      <HowToVideos admin={user?.role === "admin"} />
      {/* security */}
      <section id="security" className="bg-[#2C1E16] text-amber-50 py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={ALBUM_IMG} alt={t("Vintage family photo album secured with a brass lock and key")} loading="lazy" width={1536} height={1024} className="rounded-3xl border border-[#D4A359]/30 shadow-2xl w-full h-auto" />
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4A359]">{t("Built like a vault")}</p>
            <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2">{t("Security is not a feature. It is the foundation.")}</h2>
            <p className="mt-4 text-amber-100/70 max-w-xl">{t("Family records carry sensitive identities. VanshVriksham protects them against threats and data leakage at every layer.")}</p>
            <div className="mt-10 space-y-5">
              {security.map((s) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-[#D4A359]/15 flex items-center justify-center text-[#D4A359]">
                    <s.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold">{t(s.title)}</h3>
                    <p className="text-amber-100/60 text-sm">{t(s.desc)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* mobile teaser */}
      <section id="mobile" className="max-w-7xl mx-auto px-5 py-24 text-center">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-800/80">{t("On the roadmap")}</p>
        <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-stone-900">{t("Your lineage, in your pocket.")}</h2>
        <p className="text-stone-600 mt-4 max-w-2xl mx-auto">{t("Because everything lives in the cloud, the upcoming VanshVriksham mobile apps will offer offline sync, family voice memories and QR-code lineage sharing.")}</p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <span className="px-5 py-3 rounded-full bg-[#F4EFEB] border border-[#E6DEC3] text-sm font-medium text-stone-700">{t("Offline Sync")}</span>
          <span className="px-5 py-3 rounded-full bg-[#F4EFEB] border border-[#E6DEC3] text-sm font-medium text-stone-700">{t("Voice Memories")}</span>
          <span className="px-5 py-3 rounded-full bg-[#F4EFEB] border border-[#E6DEC3] text-sm font-medium text-stone-700">{t("QR Lineage Sharing")}</span>
        </div>
        <div className="mt-12">
          <Link to={dest}>
            <Button className="gold-btn text-[#2C1E16] font-semibold border-0 h-12 px-8 text-base" data-testid="mobile-cta-button">{t("Begin Your Family Tree ")}<ArrowRight className="ml-1" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      <GuestTreeDialog open={guestOpen} onClose={() => setGuestOpen(false)} />

      {/* footer */}
      <footer className="bg-[#1C120C] text-amber-100/60 py-12">
        <div className="max-w-7xl mx-auto px-5 mb-4 flex justify-center sm:justify-end gap-5">
          <Link to={publicPath("/about", language)} className="hover:text-[#D4A359] transition-colors">{t("About")}</Link>
            <Link to={publicPath("/contact", language)} className="text-sm hover:text-[#D4A359] transition-colors">{t("Contact Info")}</Link>
            <Link to={publicPath("/privacy-policy", language)} className="text-sm hover:text-[#D4A359] transition-colors">{t("Privacy Policy")}</Link>
        </div>
        <div className="max-w-7xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-serif text-xl font-bold text-amber-50">
            {t(" VanshVriksham")}</div>
          <p className="text-xs font-mono">{t("© 2026 VanshVriksham Heritage Vault · Security Whitepaper")}</p>
        </div>
      </footer>
    </div>
  );
}
